import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import Card from '../../../../components/card/card.component';
import Button from '../../../../components/button/button.component';
import ModalButtonBlock from '../../../../components/modal-button-block/modal-button-block.component';
import StatusBar from '../../../../components/status-bar/status-bar.component';
import ModalLabel from '../../../../components/modal-label/modal-label.component';
import ModalLayout from '../../../../components/modal-layout/modal-layout.component';
import ModalFieldBlock from '../../../../components/modal-field-block/modal-field-block.component';
import ErrorBlock from '../../../../components/error-block/error-block.component';
import Loader from '../../../../components/loader/loader.component';
import FieldSelect from '../../../../components/field-select/field-select.component';
import FieldText from '../../../../components/field-text/field-text.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import FormatService from '../../../../services/format-service';
import FormService from '../../../../services/form-service';
import { FILTER_VALUE_EMPTY, FILTER_VALUE_EXTRA } from '../../../../constants/system/filter-values';

const StatusModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isOpen,
    isSaving,
    errorMessage,
    itemActiveIds,
    statusList,
    reasonStatusList,
    statusChangeReasonList,
    onSaveForm,
    onCloseModal,
  } = props;

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [statusValue, setStatusValue]: [string, Function] = useState('');
  const [isDropdownDisabled, setIsDropdownDisabled]: [boolean | null, Function] = useState(false);
  const [isTextFieldDisabled, setIsTextFieldDisabled]: [boolean | null, Function] = useState(false);
  const [isStatusChangeReasonBlockVisible, setIsStatusChangeReasonBlockVisible]: [boolean, Function] = useState(false);
  const [selectedStatusChangeReason, setSelectedStatusChangeReason]: [string | null, Function] = useState(null);
  const [statusChangeReasonTitle, setStatusChangeReasonTitle]: [string | null, Function] = useState(null);
  const [statusChangeReasonComment, setStatusChangeReasonComment]: [string | null, Function] = useState(null);

  const initialStatusValue: string | null = statusList && statusList[0] ? statusList[0].name : null;

  const commonStatusChangeReasonList: StatusChangeReason[] = useMemo((): StatusChangeReason[] => [
    ...(statusChangeReasonList || []),
    {
      id: FILTER_VALUE_EXTRA,
      name: 'other',
      title: 'Other',
    },
  ], []);

  const menuItemStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: statusList,
  }), [statusList]);

  const menuItemStatusChangeReasonList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: commonStatusChangeReasonList,
  }), [commonStatusChangeReasonList]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const closeModal: Function = useCallback((): void => {
    if (onCloseModal) {
      onCloseModal();
    }

    setStatusValue(initialStatusValue);
    setIsStatusChangeReasonBlockVisible(false);
    setStatusChangeReasonTitle(null);
    setSelectedStatusChangeReason(null);

    closeMessage();
  }, []);

  const onStatusChange: Function = useCallback((status: string): void => {
    setStatusValue(status);

    if (reasonStatusList && reasonStatusList.length && reasonStatusList.includes(status)) {
      setIsStatusChangeReasonBlockVisible(true);
    } else {
      setIsStatusChangeReasonBlockVisible(false);
      setStatusChangeReasonTitle(null);
      setSelectedStatusChangeReason(null);
    }
  }, [reasonStatusList]);

  const onReasonDropdownChange: Function = useCallback((statusChangeReason: string): void => {
    setSelectedStatusChangeReason(statusChangeReason);

    const selectedStatusChangeReason: DealRejectReason = statusChangeReasonList && statusChangeReasonList.length
      ? statusChangeReasonList.find((dealRejectReasonItem: DealRejectReason): boolean => (
        dealRejectReasonItem.name === statusChangeReason
      )) : null;

    if (selectedStatusChangeReason) {
      setStatusChangeReasonTitle(selectedStatusChangeReason.title);
      setIsTextFieldDisabled(true);
    } else {
      setIsTextFieldDisabled(false);
    }
  }, []);

  const onStatusChangeReasonChange: Function = useCallback((statusChangeReasonValue: string): void => {
    setSelectedStatusChangeReason(null);
    setStatusChangeReasonTitle(statusChangeReasonValue);
    setIsDropdownDisabled(!!statusChangeReasonValue);
  }, []);

  const onStatusChangeReasonCommentChange: Function = useCallback((statusChangeReasonCommentValue: string): void => {
    setStatusChangeReasonComment(statusChangeReasonCommentValue);
  }, []);

  const onSaveFormClick: MouseEventHandlerFunction = useCallback((): void => {
    if (!statusValue) {
      return;
    }

    if (onSaveForm) {
      onSaveForm(
        statusValue,
        statusChangeReasonTitle || null,
        statusChangeReasonComment || null,
        (): void => {
          setStatusValue(initialStatusValue);
          setIsStatusChangeReasonBlockVisible(false);
          setStatusChangeReasonTitle(null);
          setSelectedStatusChangeReason(null);
        },
      );
    }
  }, [statusValue, statusChangeReasonTitle, statusChangeReasonComment, itemActiveIds]);

  useEffect((): void => {
    if (initialStatusValue && !statusValue) {
      setStatusValue(initialStatusValue);
    }
  }, [statusList]);

  return (
    <Fragment>
      <ModalLayout isOpen={isOpen} title="Change Status" onCloseModal={closeModal}>
        <Card isContainer>
          <Fragment>
            <ModalLabel>
              Select new status:
            </ModalLabel>
            <ModalFieldBlock>
              <FieldSelect
                isPlain
                isBordered
                withBlankElement
                isDisabled={statusList && !statusList.length}
                value={statusValue}
                onChange={(event: EventSelectChange, value: string): void => {
                  onStatusChange(value);
                }}
              >
                {menuItemStatusList}
              </FieldSelect>
            </ModalFieldBlock>
          </Fragment>
          {
            isStatusChangeReasonBlockVisible && (
              <Fragment>
                <ModalLabel>
                  Please select a reason:
                </ModalLabel>
                <ModalFieldBlock>
                  <FieldSelect
                    isPlain
                    isBordered
                    withBlankElement
                    isDisabled={isDropdownDisabled}
                    blankElementValue={FILTER_VALUE_EMPTY}
                    value={selectedStatusChangeReason}
                    onChange={(event: EventSelectChange, value: string): void => {
                      onReasonDropdownChange(value);
                    }}
                  >
                    {menuItemStatusChangeReasonList}
                  </FieldSelect>
                </ModalFieldBlock>
                <ModalLabel>
                  Or if there is no appropriate reason in the list, you can specify custom one:
                </ModalLabel>
                <ModalFieldBlock>
                  <FieldText
                    isPlain
                    isBordered
                    isDisabled={isTextFieldDisabled}
                    maxLength={FormatService.STRING_LIMIT_DEFAULT}
                    onChange={(event: React.ChangeEvent, value: string): void => {
                      onStatusChangeReasonChange(value);
                    }}
                  />
                </ModalFieldBlock>
                <ModalLabel>
                  Add comment:
                </ModalLabel>
                <ModalFieldBlock>
                  <FieldText
                    isPlain
                    isBordered
                    isMultiline
                    maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
                    minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                    onChange={(event: React.ChangeEvent, value: string): void => {
                      onStatusChangeReasonCommentChange(value);
                    }}
                  />
                </ModalFieldBlock>
              </Fragment>
            )
          }
        </Card>
        <ModalButtonBlock>
          <Button isActive isDisabled={!statusValue} onClick={onSaveFormClick}>
            Save
          </Button>
          {
            isSaving && (
              <Loader isInline isModal isSmall />
            )
          }
        </ModalButtonBlock>
        {
          !!errorMessage && (
            <ErrorBlock errorMessage={errorMessage} />
          )
        }
      </ModalLayout>
      <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
    </Fragment>
  );
});

export default StatusModal;
